"use client"

import { CheckCircle2, CircleDollarSign, Headphones, Cloud, FileJson } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { Button } from "@/shared/components/ui/button"

const features = [
  {
    icon: CircleDollarSign,
    title: "Automatiza tu facturación electrónica",
    description:
      "Genera facturas y reportes en segundos con cumplimiento normativo ante la DIAN. Controla tus procesos en tiempo real y evita rechazos con validación automática.",
    highlights: [
      "Validación automática ante la DIAN (cumplimiento garantizado).",
      "Monitor de estados, rechazos y métricas.",
      "Soporte completo para Notas Crédito y Débito.",
    ],
    image: "/images/factura.png",
    buttonText: "¡Pruébalo Ahora!",
    buttonLink: "https://app.rapyfact.com/register",
  },
  {
    icon: Headphones,
    title: "Soporte técnico y capacitación personalizada",
    description:
      "Nuestro equipo experto te acompaña paso a paso en la generación, validación y gestión de archivos JSON y facturación electrónica.",
    highlights: [
      "Atención inmediata por chat, correo y WhatsApp.",
      "Capacitación incluida en todos los planes.",
      "Responsabilidad y compromiso con la excelencia.",
    ],
    image: "/images/soporte.png",
    buttonText: "¡Pruebalo Ahora!",
    buttonLink: "https://app.rapyfact.com/register",
  },
  {
    icon: Cloud,
    title: "Almacenamiento seguro en la nube",
    description:
      "Guarda y gestiona tus facturas electrónicas, RIPS JSON y documentos clínicos con total seguridad y acceso desde cualquier lugar.",
    highlights: [
      "Cifrado extremo a extremo y backups automáticos.",
      "Acceso multiusuario con control de permisos.",
      "Almacenamiento ilimitado con alta disponibilidad.",
    ],
    image: "/images/nube.png",
    buttonText: "¡Pruebalo Ahora!",
    buttonLink: "https://app.rapyfact.com/register",
  },
  {
    icon: FileJson,
    title: "Validación de archivos JSON y generación de RIPS",
    description:
      "Asegura la exactitud y coherencia de tus datos clínicos y financieros. Cumple con la resolución 2275 del MINSALUD y genera CUV automáticamente.",
    highlights: [
      "Validación instantánea con reportes detallados.",
      "Genera RIPS JSON y CUV en segundos.",
      "Exportación masiva lista para radicación.",
    ],
    image: "/images/Validacion.png",
    buttonText: "¡Pruebalo Ahora!",
    buttonLink: "https://app.rapyfact.com/register",
  },
]

export function FeaturesSection() {
  const [visibleFeatures, setVisibleFeatures] = useState<number[]>([])
  const featureRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = featureRefs.current.indexOf(entry.target as HTMLDivElement)
            if (index !== -1 && !visibleFeatures.includes(index)) {
              setVisibleFeatures((prev) => [...prev, index])
            }
          }
        })
      },
      { threshold: 0.1 }
    )

    featureRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref)
    })

    return () => observer.disconnect()
  }, [visibleFeatures])

  return (
    <section className="relative overflow-hidden">
      {features.map((feature, index) => (
        <div
          key={index}
          className={`relative py-16 lg:py-20 ${
            index % 2 === 0
              ? "bg-gradient-to-b from-muted/30 via-muted/50 to-muted/30"
              : "bg-gradient-to-b from-background via-background/95 to-background"
          }`}
        >
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(to right, hsl(var(--foreground)) 1px, transparent 1px),
                               linear-gradient(to bottom, hsl(var(--foreground)) 1px, transparent 1px)`,
              backgroundSize: "64px 64px",
            }}
          />

          <div className="container mx-auto px-4 lg:px-8 relative">
            <div
              ref={(el) => {
                featureRefs.current[index] = el
              }}
              className={`grid lg:grid-cols-2 gap-12 lg:gap-16 xl:gap-20 items-center ${
                index % 2 === 1 ? "lg:grid-flow-dense" : ""
              }`}
            >
              {/* Texto */}
              <div
                className={`space-y-6 ${
                  index % 2 === 1 ? "lg:col-start-2" : ""
                } transition-all duration-700 ${
                  visibleFeatures.includes(index)
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                }`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20">
                  <feature.icon className="w-8 h-8 text-primary" strokeWidth={2} />
                </div>

                <div className="space-y-4">
                  <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
                    {feature.title}
                  </h3>
                  <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>

                {feature.highlights && (
                  <ul className="space-y-3 pt-2">
                    {feature.highlights.map((highlight, i) => (
                      <li
                        key={i}
                        className={`flex items-start gap-3 transition-all duration-500 ${
                          visibleFeatures.includes(index)
                            ? "opacity-100 translate-x-0"
                            : "opacity-0 -translate-x-4"
                        }`}
                        style={{ transitionDelay: `${(i + 1) * 100}ms` }}
                      >
                        <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" strokeWidth={2} />
                        <span className="text-xl sm:text-xl text-foreground/90">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Botón */}
                {feature.buttonLink && (
                  <div className="pt-6">
                    <a href={feature.buttonLink} target="_blank" rel="noopener noreferrer">
                      <Button
                        size="lg"
                        className="bg-primary hover:bg-primary/90 text-primary-foreground group text-lg px-8 py-7 shadow-lg"
                      >
                        {feature.buttonText}
                      </Button>
                    </a>
                  </div>
                )}
              </div>

              {/* Imagen transparente (sin fondo, sin bordes, sin sombras) */}
              <div
                className={`${index % 2 === 1 ? "lg:col-start-1 lg:row-start-1" : ""} transition-all duration-700 ${
                  visibleFeatures.includes(index)
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: "200ms" }}
              >
                <div className="relative group">
                  <div className="relative aspect-[4/3] overflow-hidden rounded-3xl bg-transparent transition-transform duration-500 group-hover:scale-[1.02]">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </section>
  )
}
